/**
 * ================================================================
 * ORGANIZATION SERVICE - Unified Service (Phase 1 Refactor)
 * ================================================================
 *
 * Handles CRUD for "Organization" Entities (Clubs/Venues).
 * - Separated from Organizer (Persona) logic.
 * - Uses Strict Zod Types.
 *
 * @module OrganizationService
 */

import apiClient from "@/lib/apiClient";
import {
  organizations as Organization,
  organizationsSchema,
} from "@mebi/schemas";

// Extended Types for UI (Counts, etc)
export interface OrganizationWithCounts extends Organization {
  member_count: number;
  tournament_count: number;
  organizer_name?: string; // Owner name
  organizer_avatar_url?: string;
  // Admin fields
  verified_status: "pending" | "verified" | "rejected" | "no_organization";
  status?: string; // Legacy alias
}

export interface UpdateOrganizationRequest {
  name?: string;
  description?: string;
  logo_url?: string;
  banner_url?: string;
  is_public?: boolean;
  location_name?: string;
  location_address?: string;
  // Add other updateable fields from schema if needed
}

export interface OrganizationApiResult<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

export const organizationService = {
  async updateVerificationStatus(
    organizationId: string,
    status: "verified" | "rejected",
    note?: string,
  ): Promise<OrganizationApiResult<OrganizationWithCounts>> {
    try {
      const payload: Record<string, string> = { status };
      if (note) payload.note = note;
      const { data: result } = await apiClient.put(
        `/admin/organizations/${organizationId}/verification-status`,
        payload,
      );
      if (!result.success)
        throw new Error(result.error || "Failed to update status");
      return result;
    } catch (error: unknown) {
      const err = error as Error;
      throw new Error(err.message || "Update status failed");
    }
  },

  async getAllOrganizations(
    status?: string,
  ): Promise<OrganizationWithCounts[]> {
    try {
      const params = status ? { status } : {};
      const { data: result } = await apiClient.get("/admin/organizations", {
        params,
      });
      return result.success ? result.data : [];
    } catch (error) {
      console.error("getAllOrganizations error:", error);
      return [];
    }
  },

  /**
   * Get Public Organizations (Browse Screen)
   */
  async getPublicOrganizations(): Promise<OrganizationWithCounts[]> {
    try {
      const { data } = await apiClient.get("/organizations");
      // Backend should return array directly or wrapped.
      // Assuming array based on previous client inspection.
      if (Array.isArray(data)) {
        return data as OrganizationWithCounts[];
      }
      if (data?.data && Array.isArray(data.data)) {
        return data.data as OrganizationWithCounts[];
      }
      return [];
    } catch (error) {
      console.error("[OrganizationService] Public list error:", error);
      return [];
    }
  },

  /**
   * Get Organization by Slug or ID (Public Detail)
   */
  async getOrganizationBySlug(
    slugOrId: string,
  ): Promise<OrganizationWithCounts | null> {
    try {
      const { data } = await apiClient.get(`/organizations/${slugOrId}`);
      // Use passthrough for OrganizationWithCounts if extended fields exist
      return data as OrganizationWithCounts; // Keeping as cast for now since schema is base.
      // Or prioritize base validation:
      // const base = organizationsSchema.parse(data);
      // return { ...base, ...data }; // Merge back extra counts
    } catch (error) {
      console.error(
        "[OrganizationService] getOrganizationBySlug error:",
        error,
      );
      return null;
    }
  },

  /**
   * Get My Organizations (Manager View)
   * Returns list of clubs owned by current user.
   */
  async getMyOrganizations(): Promise<OrganizationApiResult<Organization[]>> {
    try {
      // Note: Endpoint changed to be more RESTful if possible,
      // but keeping '/organizer/organizations' compatibility for now
      // or expecting backend refactor?
      // User said backend is 100% NestJS.
      // Standard: GET /organizations/me or /organizer/organizations
      const { data } = await apiClient.get("/organizer/organizations");
      return {
        success: true,
        message: "Success",
        data: data.data || data, // Handle wrapper
      };
    } catch (error: unknown) {
      const err = error as Error;
      return {
        success: false,
        message: "Không thể lấy danh sách tổ chức",
        error: err.message,
      };
    }
  },

  /**
   * Create Organization (Create a Club)
   * NOTE: 'register' in previous API was for Organizer Persona.
   * This is for creating a new Club Entity.
   */
  async createOrganization(data: {
    name: string;
    description?: string;
  }): Promise<OrganizationApiResult<Organization>> {
    try {
      const { data: result } = await apiClient.post("/organizations", data);
      return {
        success: true,
        message: "Tạo tổ chức thành công",
        data: organizationsSchema.partial().parse(result) as Organization,
      };
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { message?: string } };
        message: string;
      };
      return {
        success: false,
        message: err.response?.data?.message || "Không thể tạo tổ chức",
        error: err.message,
      };
    }
  },

  /**
   * Update Organization Details
   */
  async updateOrganization(
    id: string,
    data: UpdateOrganizationRequest,
  ): Promise<OrganizationApiResult<Organization>> {
    try {
      // Sanitize blobs just in case
      if (data.logo_url?.startsWith("blob:")) delete data.logo_url;
      if (data.banner_url?.startsWith("blob:")) delete data.banner_url;

      const { data: result } = await apiClient.put(
        `/organizer/organizations/${id}`,
        data,
      );
      const rawData = result.data || result;
      return {
        success: true,
        message: "Cập nhật thành công",
        data: organizationsSchema.partial().parse(rawData) as Organization,
      };
    } catch (error: unknown) {
      const err = error as Error;
      return {
        success: false,
        message: "Cập nhật thất bại",
        error: err.message,
      };
    }
  },

  /**
   * Delete Organization
   */
  async deleteOrganization(id: string): Promise<OrganizationApiResult> {
    try {
      const { data } = await apiClient.delete(`/organizer/organizations/${id}`);
      return data;
    } catch (error: unknown) {
      const err = error as Error;
      return {
        success: false,
        message: "Xóa thất bại",
        error: err.message,
      };
    }
  },
};

export default organizationService;
